import { useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useCrearCheckoutSessionMutation, useGetPlanesSuscripcionesQuery, PlanSuscripcion } from "src/gql/schema"
import SharedDropdown, { Option } from "src/shared/components/forms/selelct-dropdown/Dropdown"
import ResultCard from "./components/ResultCard/ResultCard"
import CalculadoraHeader from "./components/CalculadoraHeader/CalculadoraHeader"
import { useProfile } from "src/shared/hooks/useProfile"
import { storePendingConfigProvisioning } from "src/utils/provisioning"
import { readSelectedPlan } from "src/utils/selectedPlan"
import "./Calculadora.css"

const habitacionesOptions: Option[] = [
    { label: "1-15 habitaciones", value: "15" },
    { label: "16-48 habitaciones", value: "48" },
    { label: "49-80 habitaciones", value: "80" },
    { label: "81-120 habitaciones", value: "120" },
]

const mesasOptions: Option[] = [
    { label: "0 mesas", value: "0" },
    { label: "1-15 mesas", value: "15" },
    { label: "16-42 mesas", value: "42" },
]

function Calculadora(): JSX.Element {
    const { hoteles, prueba_gratis_iniciada, myProfile } = useProfile()
    const [habitaciones, setHabitaciones] = useState("15")
    const [mesas, setMesas] = useState("0")
    const [checkoutError, setCheckoutError] = useState<string | null>(null)
    const [planSeleccionadoNoDisponible, setPlanSeleccionadoNoDisponible] = useState(false)
    const [searchParams] = useSearchParams()
    const autoCheckoutTriggered = useRef(false)

    const prueba_gratis = Object.values(myProfile).length > 0 && myProfile?.prueba_consumida_en !== undefined && !prueba_gratis_iniciada

    const fromCalculatorLanding = searchParams.get("source") === "calculatorLanding"
    const selectedPlanId = fromCalculatorLanding ? readSelectedPlan() : null

    const { data } = useGetPlanesSuscripcionesQuery()
    const planes = data?.planesSuscripciones ?? []

    const [crearCheckoutSession, { loading: creandoCheckout }] = useCrearCheckoutSessionMutation()

    const maxHab = parseInt(habitaciones, 10)
    const maxMesas = parseInt(mesas, 10)

    // Algunos planes tienen un duplicado "test mode": por ahora es el único
    // que tiene un price_id de Stripe válido para este ambiente, así que se
    // prioriza sobre el plan "oficial" mientras backend termina de
    // configurar los precios de producción.
    //cambiar esta configuracion antes de pasar a produccion, para que se priorice el plan oficial y no el test mode
    //importante-tener en cuentaaa
    const findPlan = (hab: number, mesasCount: number) => {
        const candidates = planes.filter((p) => p.numero_max_habitaciones === hab && p.numero_max_mesas === mesasCount)
        return candidates.find((p) => p.nombre.toLowerCase().includes("test mode")) ?? candidates[0] ?? null
    }

    const matchedPlan = findPlan(maxHab, maxMesas)
    const basePlan = findPlan(maxHab, 0)

    const habitacionesPrice = basePlan?.costo ?? 0
    const mesasPrice = matchedPlan ? matchedPlan.costo - habitacionesPrice : 0

    const iniciarCheckout = async (plan: PlanSuscripcion) => {
        setCheckoutError(null)

        try {
            const provisioningState = self.crypto.randomUUID()
            const successUrl = new URL("/auth/config/provisioning", window.location.origin)
            successUrl.searchParams.set("source", "stripe")
            successUrl.searchParams.set("state", provisioningState)

            const { data: checkoutData } = await crearCheckoutSession({
                variables: {
                    datos: {
                        plan_suscripcion_id: plan.plan_suscripcion_id,
                        success_url: successUrl.toString(),
                        cancel_url: `${window.location.origin}/calculadora`,
                    },
                },
            })

            if (checkoutData?.crearCheckoutSession) {
                storePendingConfigProvisioning(
                    (hoteles ?? []).map((hotel) => hotel.hotel_id).filter(Boolean),
                    provisioningState
                )
                window.location.href = checkoutData.crearCheckoutSession
            }
        } catch (err) {
            setCheckoutError(err instanceof Error ? err.message : "No se pudo iniciar el pago. Intenta de nuevo.")
        }
    }

    const handleContratar = () => {
        if (!matchedPlan) return
        iniciarCheckout(matchedPlan)
    }

    useEffect(() => {
        if (!fromCalculatorLanding || !selectedPlanId || autoCheckoutTriggered.current || !data) return

        const rawPlan = planes.find((p) => p.plan_suscripcion_id === selectedPlanId)
        if (!rawPlan) {
            setPlanSeleccionadoNoDisponible(true)
            return
        }

        // El id que llega por query param puede apuntar al plan "oficial" (price live),
        // así que se re-resuelve por habitaciones/mesas para heredar la misma prioridad
        // de "test mode" que usa la selección manual (findPlan) en este ambiente.
        const plan = findPlan(rawPlan.numero_max_habitaciones, rawPlan.numero_max_mesas) ?? rawPlan

        autoCheckoutTriggered.current = true
        iniciarCheckout(plan)
    }, [fromCalculatorLanding, selectedPlanId, data])

    const redirigiendoAStripe = fromCalculatorLanding && !!selectedPlanId && !planSeleccionadoNoDisponible && !checkoutError

    if (redirigiendoAStripe) {
        return (
            <>
                <CalculadoraHeader />
                <section className="calculadora-screen">
                    <p className="calculadora-desc">Estamos preparando tu pago, te redirigiremos a Stripe en un momento…</p>
                </section>
            </>
        )
    }

    return (
        <>
            <CalculadoraHeader />
            <section className="calculadora-screen">
                <div className="calculadora-layout">
                    <div className="calculadora-left">
                        <img src={require("src/assets/png/logo_md.png")} alt="Easyroom" className="calculadora-logo" />
                        <h1 className="calculadora-title">Adquiere tu licencia</h1>
                        {planSeleccionadoNoDisponible && (
                            <p className="calculadora-desc calculadora-desc--error">
                                El plan que seleccionaste ya no está disponible. Elige uno de los planes disponibles.
                            </p>
                        )}
                        <p className="calculadora-desc">
                            Obtén acceso completo a todas las funcionalidades de{" "}
                            <span className="calculadora-desc__brand">Easyroom</span> y comienza a gestionar tu
                            establecimiento de forma más simple, eficiente y profesional.
                        </p>
                        <div className="calculadora-dropdowns">
                            <SharedDropdown
                                label="Número de habitaciones"
                                options={habitacionesOptions}
                                value={habitaciones}
                                onClick={(option) => setHabitaciones(String(option.value))}
                                placeholder="Selecciona"
                            />
                            <SharedDropdown
                                label="Número de mesas para Restaurante (Opcional)"
                                options={mesasOptions}
                                value={mesas}
                                onClick={(option) => setMesas(String(option.value))}
                                placeholder="Selecciona"
                            />
                        </div>
                        <a
                            href="https://easyroom.io/pricing"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="calculadora-link"
                        >
                            Ver todo lo que incluye
                        </a>
                    </div>

                    <div className="calculadora-right">
                        <ResultCard
                            planName={matchedPlan?.nombre.replace(/\s*test mode$/i, "") ?? ""}
                            price={matchedPlan?.costo ?? 0}
                            habitacionesPrice={habitacionesPrice}
                            mesasPrice={mesasPrice}
                            onContratar={handleContratar}
                            loading={creandoCheckout}
                            error={checkoutError}
                            btnLabel={prueba_gratis ? "Comienza gratis" : "Contratar plan"}
                        />
                    </div>
                </div>
            </section>
        </>
    )
}

export default Calculadora
